import { useEffect, useState } from 'react'

// Calcolatrice full-screen. Accumulatore single-op (left-to-right, no priority).
// `initial` viene mostrato come current al primo render. DONE chiama onDone
// con la stringa del display corrente (forzando il calcolo pendente).
export function Calculator({ initial, onCancel, onDone }) {
  const [current, setCurrent]   = useState(() => normalize(initial))
  const [acc, setAcc]           = useState(null)   // accumulatore parziale (string)
  const [pending, setPending]   = useState(null)   // op in attesa: '+','-','×','÷'
  const [justOp, setJustOp]     = useState(false)  // true subito dopo un operatore

  // Same visualViewport trick as the tx modal — calc should sit above the
  // keyboard area (though no input gets focused, we keep symmetry).
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const update = () => {
      document.documentElement.style.setProperty('--vv-h', `${vv.height}px`)
    }
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
    }
  }, [])

  function pressDigit(d) {
    if (justOp) {
      setCurrent(d === '00' ? '0' : d)
      setJustOp(false)
      return
    }
    if (d === '.') {
      if (current.includes('.')) return
      setCurrent((current || '0') + '.')
      return
    }
    if (d === '00') {
      if (!current || current === '0' || current === '-0') return
      setCurrent(current + '00')
      return
    }
    if (current === '0') setCurrent(d)
    else if (current === '-0') setCurrent('-' + d)
    else setCurrent(current + d)
  }

  function pressOp(op) {
    if (acc != null && pending && !justOp) {
      // Pipeline: applica op precedente
      const r = applyOp(acc, current, pending)
      setAcc(fmt(r))
      setCurrent(fmt(r))
    } else if (current) {
      setAcc(current)
    }
    setPending(op)
    setJustOp(true)
  }

  function pressEquals() {
    if (pending && acc != null) {
      const r = applyOp(acc, current, pending)
      setCurrent(fmt(r))
      setAcc(null)
      setPending(null)
      setJustOp(false)
    }
  }

  function pressClear() {
    setCurrent('0')
    setAcc(null)
    setPending(null)
    setJustOp(false)
  }

  function pressBack() {
    if (justOp) return
    if (!current || current.length <= 1 || (current.length === 2 && current.startsWith('-'))) {
      setCurrent('0')
    } else {
      setCurrent(current.slice(0, -1))
    }
  }

  function pressDone() {
    // Forza qualsiasi calcolo pending prima di chiudere
    let final = current
    if (pending && acc != null && !justOp) {
      final = fmt(applyOp(acc, current, pending))
    } else if (justOp && acc != null) {
      final = acc
    }
    onDone(final)
  }

  return (
    <div className="calc">
      <header className="calc-header">
        <button type="button" className="calc-close" onClick={onCancel} aria-label="Close">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </header>
      <div className="calc-display">
        {pending && acc != null && (
          <div className="calc-display-pending">{acc} {pending}</div>
        )}
        <div className="calc-display-current">{current || '0'}</div>
      </div>
      <div className="calc-grid">
        <Btn className="calc-fn"  onClick={pressClear}>AC</Btn>
        <Btn className="calc-op"  onClick={() => pressOp('÷')}>÷</Btn>
        <Btn className="calc-op"  onClick={() => pressOp('×')}>×</Btn>
        <Btn className="calc-fn"  onClick={pressBack}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 5H9l-7 7 7 7h13a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1z" />
            <line x1="18" y1="9" x2="12" y2="15" />
            <line x1="12" y1="9" x2="18" y2="15" />
          </svg>
        </Btn>

        <Btn onClick={() => pressDigit('7')}>7</Btn>
        <Btn onClick={() => pressDigit('8')}>8</Btn>
        <Btn onClick={() => pressDigit('9')}>9</Btn>
        <Btn className="calc-op"  onClick={() => pressOp('-')}>−</Btn>

        <Btn onClick={() => pressDigit('4')}>4</Btn>
        <Btn onClick={() => pressDigit('5')}>5</Btn>
        <Btn onClick={() => pressDigit('6')}>6</Btn>
        <Btn className="calc-op"  onClick={() => pressOp('+')}>+</Btn>

        <Btn onClick={() => pressDigit('1')}>1</Btn>
        <Btn onClick={() => pressDigit('2')}>2</Btn>
        <Btn onClick={() => pressDigit('3')}>3</Btn>
        <Btn className="calc-op"  onClick={pressEquals}>=</Btn>

        <Btn onClick={() => pressDigit('00')}>00</Btn>
        <Btn onClick={() => pressDigit('0')}>0</Btn>
        <Btn onClick={() => pressDigit('.')}>.</Btn>
        <Btn className="calc-done" onClick={pressDone}>DONE</Btn>
      </div>
    </div>
  )
}

function Btn({ onClick, className = '', children }) {
  return (
    <button type="button" className={`calc-btn ${className}`} onClick={onClick}>
      {children}
    </button>
  )
}

function normalize(v) {
  if (v == null) return '0'
  const s = String(v).trim()
  return s === '' ? '0' : s
}

function applyOp(aStr, bStr, op) {
  const a = parseFloat(aStr)
  const b = parseFloat(bStr)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0
  switch (op) {
    case '+': return a + b
    case '-': return a - b
    case '×': return a * b
    case '÷': return b === 0 ? 0 : a / b
    default:  return b
  }
}

// Format result removing trailing zeros and clamping to 8 decimals max
function fmt(n) {
  if (!Number.isFinite(n)) return '0'
  const rounded = Math.round(n * 1e8) / 1e8
  return String(rounded)
}
